import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Percent, Pencil, Plus, RefreshCw, Trash2, BarChart3 } from 'lucide-react'
import {
  useDiscountRules,
  useCreateDiscountRule,
  useUpdateDiscountRule,
  useDeleteDiscountRule,
  useDiscountRuleAnalytics,
} from '@/hooks/data/useDiscounts'
import { useProductListAdmin, useProductCategories } from '@/lib/productCatalog'
import type { DiscountRule, DiscountRuleInput, DiscountRuleType } from '@/types/ecommerce'
import EditModal from '@/components/crud/EditModal'
import { useDeleteConfirm } from '@/components/crud/DeleteConfirm'
import { InlineToggle } from '@/components/crud/InlineToggle'
import { toast } from '@/ui/Toast'
import { userMessage } from '@/api/errors'

const TYPE_LABELS: Record<DiscountRuleType, string> = {
  Percentage: 'Percentage off',
  FixedAmount: 'Fixed amount off',
  BuyXGetY: 'Buy X, get Y free',
  Volume: 'Volume discount',
  Bundle: 'Bundle discount',
}

function toDateInput(iso: string) {
  return iso ? iso.slice(0, 16) : ''
}
function toIso(dateLocal: string) {
  return dateLocal ? new Date(dateLocal).toISOString() : new Date().toISOString()
}

function DiscountsPage() {
  const { data: rules = [], isLoading, isError, error, isFetching, refetch } = useDiscountRules()
  const { data: productPage } = useProductListAdmin({ status: 'Published', pageSize: 200 })
  const { data: categories = [] } = useProductCategories()

  const create = useCreateDiscountRule()
  const update = useUpdateDiscountRule()
  const del = useDeleteDiscountRule()
  const confirmDelete = useDeleteConfirm()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<DiscountRule | null>(null)
  const [analyticsFor, setAnalyticsFor] = useState<DiscountRule | null>(null)
  const { data: analytics } = useDiscountRuleAnalytics(analyticsFor?.id ?? null)

  const productOptions = useMemo(
    () => (productPage?.items ?? []).map((p) => ({ value: p.id, label: p.name })),
    [productPage],
  )
  const categoryOptions = useMemo(() => categories.map((c) => ({ value: c.id, label: c.name })), [categories])

  const fields = [
    { key: 'name', label: 'Name', required: true },
    {
      key: 'type', label: 'Type', type: 'select' as const, required: true,
      options: (Object.keys(TYPE_LABELS) as DiscountRuleType[]).map((t) => ({ value: t, label: TYPE_LABELS[t] })),
    },
    { key: 'value', label: 'Value (% or $, per type)', type: 'number' as const, required: true, hint: 'Percentage/Volume/Bundle: 0-100. FixedAmount: dollars. Unused for BuyXGetY.' },
    { key: 'code', label: 'Coupon Code', placeholder: 'Leave blank for an automatic discount (no code needed)' },
    { key: 'minCartTotal', label: 'Min Cart Total ($)', type: 'number' as const },
    { key: 'minQuantity', label: 'Min Quantity', type: 'number' as const },
    { key: 'productIds', label: 'Products (blank = all)', type: 'multiselect' as const, options: productOptions },
    { key: 'categoryIds', label: 'Categories (blank = all)', type: 'multiselect' as const, options: categoryOptions },
    { key: 'buyQuantity', label: 'Buy Quantity (BuyXGetY only)', type: 'number' as const },
    { key: 'getQuantity', label: 'Get Quantity Free (BuyXGetY only)', type: 'number' as const },
    { key: 'startsAt', label: 'Starts At', type: 'datetime-local' as const, required: true },
    { key: 'endsAt', label: 'Ends At', type: 'datetime-local' as const, required: true },
    { key: 'active', label: 'Active', type: 'select' as const, required: true, options: [{ value: 'true', label: 'Active' }, { value: 'false', label: 'Inactive' }] },
    { key: 'usageLimit', label: 'Total Usage Limit', type: 'number' as const },
    { key: 'perCustomerLimit', label: 'Per-Customer Limit', type: 'number' as const },
    { key: 'priority', label: 'Priority (tie-break only)', type: 'number' as const, required: true },
  ]

  const toInput = (values: Record<string, any>): DiscountRuleInput => ({
    name: values.name,
    type: values.type,
    value: Number(values.value) || 0,
    code: values.code?.trim() || null,
    minCartTotal: values.minCartTotal !== '' && values.minCartTotal != null ? Number(values.minCartTotal) : null,
    minQuantity: values.minQuantity !== '' && values.minQuantity != null ? Number(values.minQuantity) : null,
    productIds: Array.isArray(values.productIds) ? values.productIds : [],
    categoryIds: Array.isArray(values.categoryIds) ? values.categoryIds : [],
    buyQuantity: values.buyQuantity !== '' && values.buyQuantity != null ? Number(values.buyQuantity) : null,
    getQuantity: values.getQuantity !== '' && values.getQuantity != null ? Number(values.getQuantity) : null,
    startsAt: toIso(values.startsAt),
    endsAt: toIso(values.endsAt),
    active: values.active === 'true',
    usageLimit: values.usageLimit !== '' && values.usageLimit != null ? Number(values.usageLimit) : null,
    perCustomerLimit: values.perCustomerLimit !== '' && values.perCustomerLimit != null ? Number(values.perCustomerLimit) : null,
    priority: Number(values.priority) || 0,
  })

  return (
    <div className="p-2 md:p-4 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Percent className="w-6 h-6" /> Discount Rules
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            The real checkout-time discount engine (DiscountsController) — stacks with loyalty/referral, applied fresh on every order.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setCreating(true)} className="flex items-center gap-1 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm">
            <Plus className="w-4 h-4" /> New Rule
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading discount rules…</p>
        ) : isError ? (
          <p className="p-6 text-red-600">Failed to load: {(error as Error)?.message}.</p>
        ) : rules.length === 0 ? (
          <p className="p-6 text-gray-500">No discount rules yet.</p>
        ) : (
          <table className="min-w-full text-left text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Value</th>
                <th className="px-4 py-2">Code</th>
                <th className="px-4 py-2">Usage</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 dark:border-gray-700">
                  <td className="px-4 py-3 font-medium text-gray-800 dark:text-gray-100">{r.name}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{TYPE_LABELS[r.type]}</td>
                  <td className="px-4 py-3">{r.type === 'FixedAmount' ? `$${r.value}` : r.type === 'BuyXGetY' ? `${r.buyQuantity}+${r.getQuantity}` : `${r.value}%`}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.code ?? <span className="text-gray-400">automatic</span>}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{r.usageCount}{r.usageLimit ? ` / ${r.usageLimit}` : ''}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <InlineToggle
                        checked={r.active}
                        onToggle={(next) =>
                          update.mutateAsync({
                            id: r.id,
                            body: {
                              name: r.name,
                              type: r.type,
                              value: r.value,
                              code: r.code,
                              minCartTotal: r.minCartTotal,
                              minQuantity: r.minQuantity,
                              productIds: r.productIds,
                              categoryIds: r.categoryIds,
                              buyQuantity: r.buyQuantity,
                              getQuantity: r.getQuantity,
                              startsAt: r.startsAt,
                              endsAt: r.endsAt,
                              active: next,
                              usageLimit: r.usageLimit,
                              perCustomerLimit: r.perCustomerLimit,
                              priority: r.priority,
                            },
                          })
                        }
                      />
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.isRunning ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {r.isRunning ? 'Running' : r.active ? 'Scheduled/Expired' : 'Inactive'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setAnalyticsFor(r)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Analytics">
                        <BarChart3 className="w-4 h-4" />
                      </button>
                      <button onClick={() => setEditing(r)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500" title="Edit rule">
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() =>
                          confirmDelete(r.name, async () => {
                            try {
                              await del.mutateAsync(r.id)
                            } catch (err) {
                              toast.error(userMessage(err))
                            }
                          })
                        }
                        className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500"
                        title="Delete rule"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {analyticsFor && (
        <div className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-gray-800 dark:text-white">Analytics — {analyticsFor.name}</h2>
            <button onClick={() => setAnalyticsFor(null)} className="text-xs text-gray-400 hover:underline">Close</button>
          </div>
          {analytics ? (
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div><div className="text-gray-500">Times redeemed</div><div className="text-xl font-bold">{analytics.timesRedeemed}</div></div>
              <div><div className="text-gray-500">Unique customers</div><div className="text-xl font-bold">{analytics.uniqueCustomers}</div></div>
              <div><div className="text-gray-500">Total discounted</div><div className="text-xl font-bold">${analytics.totalDiscounted.toFixed(2)}</div></div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Loading…</p>
          )}
        </div>
      )}

      <EditModal
        isOpen={creating}
        onClose={() => setCreating(false)}
        title="New Discount Rule"
        fields={fields}
        initialValues={{ active: 'true', productIds: [], categoryIds: [], priority: 0, type: 'Percentage' }}
        submitLabel="Create"
        onSubmit={async (values) => {
          try {
            await create.mutateAsync(toInput(values))
          } catch (err) {
            toast.error(userMessage(err))
            throw err
          }
        }}
      />

      <EditModal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title="Edit Discount Rule"
        description={editing?.name}
        fields={fields}
        initialValues={
          editing
            ? {
                name: editing.name,
                type: editing.type,
                value: editing.value,
                code: editing.code ?? '',
                minCartTotal: editing.minCartTotal ?? '',
                minQuantity: editing.minQuantity ?? '',
                productIds: editing.productIds,
                categoryIds: editing.categoryIds,
                buyQuantity: editing.buyQuantity ?? '',
                getQuantity: editing.getQuantity ?? '',
                startsAt: toDateInput(editing.startsAt),
                endsAt: toDateInput(editing.endsAt),
                active: editing.active ? 'true' : 'false',
                usageLimit: editing.usageLimit ?? '',
                perCustomerLimit: editing.perCustomerLimit ?? '',
                priority: editing.priority,
              }
            : {}
        }
        onSubmit={async (values) => {
          if (!editing) return
          try {
            await update.mutateAsync({ id: editing.id, body: toInput(values) })
          } catch (err) {
            toast.error(userMessage(err))
            throw err
          }
        }}
      />
    </div>
  )
}

export const Route = createFileRoute('/(admin)/discounts/')({
  component: DiscountsPage,
})
